import * as React from "react";
import { useNavigate } from "react-router";

import { Context } from "@datadog/browser-logs";
import AddBoxIcon from "@mui/icons-material/AddBox";
import GroupsIcon from "@mui/icons-material/Groups";
import HistoryIcon from "@mui/icons-material/History";
import LayersIcon from "@mui/icons-material/Layers";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SchoolIcon from "@mui/icons-material/School";
import SpeedIcon from "@mui/icons-material/Speed";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import StarIcon from "@mui/icons-material/Star";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import type { SvgIconProps } from "@mui/material/SvgIcon";

import userMonitor from "../../userMonitor";
import useDialog from "../Dialog/useDialog";
import { hasOfficialDuprRating } from "../Journeys/JourneyAssessment/duprRating";
import usePadelRolloutPhase from "../Journeys/PadelAssessment/usePadelRolloutPhase";
import ModuleIcon from "../Trainer/AppWorkouts/apps/11-module/Icon";
import SingleShotIcon from "../Trainer/AppWorkouts/apps/5-single-shot/Icon";
import MultiLevelIcon from "../Trainer/AppWorkouts/apps/9-multi-level/Icon";
import AIIcon from "../Trainer/AppWorkouts/apps/shared/AIIcon";
import FilterState from "../Trainer/AppWorkouts/apps/shared/filterState";
import { useSelectedSport } from "../common/context/sport";
import { useCurrentUser } from "../hooks/currentUser";

import ClubCard from "./ClubCard";
import { useHomeDupr } from "./HomeDuprContext";
import { useHomePti } from "./HomePtiContext";
import {
    duprAssessmentLabel,
    mainWorkoutItems,
    PADEL_ASSESSMENT_FEATURE,
    PersonalItem,
    personalItems,
    SEGMENT_COLLECTION_ITEM_PREFIX,
    SEGMENT_COLLECTIONS,
    withSegmentCollections,
} from "./HomeScreenItems";
import SectionHeader from "./SectionHeader";
import SimpleCard from "./SimpleCard";
import WorkoutListCard from "./WorkoutListCard";

interface CommunityClub {
    name: string;
    label: string;
    providerId: number;
}

interface NewHomeScreenLibraryProps {
    homeClub: CommunityClub | null;
    homeClubClasses: boolean;
    homeClubWorkouts: boolean;
    trainerClub: CommunityClub | null;
}

const iconMap: Record<string, React.ReactNode> = {
    performance_dashboard: (
        <QueryStatsIcon sx={{ color: "#3b82f6", fontSize: "1.5rem" }} />
    ),
    quick_start: (
        <SportsTennisIcon sx={{ color: "#4ade80", fontSize: "1.5rem" }} />
    ),
    classes_at_location: (
        <SchoolIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />
    ),
    ai_workouts: <AIIcon sx={{ color: "#a855f7", fontSize: "1.5rem" }} />,
    multi_level: (
        <MultiLevelIcon sx={{ color: "#56D5EF", fontSize: "1.5rem" }} />
    ),
    skill_building: (
        <ModuleIcon sx={{ color: "#DA30D8", fontSize: "1.5rem" }} />
    ),
    single_shots: (
        <SingleShotIcon sx={{ color: "#EFA511", fontSize: "1.5rem" }} />
    ),
    all_workouts: <LayersIcon sx={{ color: "#ffffff", fontSize: "1.5rem" }} />,
    long_form_assessment: (
        <SpeedIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />
    ),
    padel_assessment: (
        <SpeedIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />
    ),
};

const personalIconMap: Record<string, React.ReactNode> = {
    recently_played: (
        <HistoryIcon sx={{ color: "#ffffff", fontSize: "1.5rem" }} />
    ),
    favorites: <StarIcon sx={{ color: "#ffffff", fontSize: "1.5rem" }} />,
    my_workouts: <AddBoxIcon sx={{ color: "#ffffff", fontSize: "1.5rem" }} />,
    pti_stats: <TrendingUpIcon sx={{ color: "#ffffff", fontSize: "1.5rem" }} />,
    dupr_dashboard: (
        <LeaderboardIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />
    ),
    padel_dashboard: (
        <LeaderboardIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />
    ),
};

function PtiTrendIcon({
    trend,
    ...props
}: { trend: -1 | 0 | 1 } & SvgIconProps): React.JSX.Element | null {
    switch (trend) {
        case -1:
            return <TrendingDownIcon {...props} />;
        case 0:
            return null;
        case 1:
        default:
            return (
                <TrendingUpIcon
                    {...props}
                    sx={{
                        color: (theme) =>
                            `${theme.palette.success.main} !important`,
                        ...props.sx,
                    }}
                />
            );
    }
}

export default function NewHomeScreenLibrary({
    homeClub,
    homeClubClasses,
    homeClubWorkouts,
    trainerClub,
}: NewHomeScreenLibraryProps): React.JSX.Element {
    const navigate = useNavigate();
    const { selected: sport } = useSelectedSport();
    const { features } = useCurrentUser();
    const { setDialogType } = useDialog();
    // PTI comes from its own request, so a slow or failing APTA lookup no longer
    // holds up — or silently blanks — the rest of this screen.
    const { rating, hasAptaId, state: ptiState } = useHomePti();

    // DUPR state drives the assessment entry's copy and placement:
    //  - not linked            → "Don't have a DUPR? Get Rated"
    //  - linked, unrated       → "Get your official DUPR rating"
    //  - officially rated      → "Retake your DUPR assessment", and the DUPR
    //                            Dashboard is added under Personal
    //  - already submitted     → the entry drops out entirely; only the Personal
    //                            Dashboard row remains
    const {
        profile: duprProfile,
        hasSubmitted: duprSubmitted,
        loading: duprLoading,
    } = useHomeDupr();
    const { phase: padelPhase, loading: padelPhaseLoading } =
        usePadelRolloutPhase();
    const duprLinked = duprProfile != null;
    const duprRated = duprProfile ? hasOfficialDuprRating(duprProfile) : false;
    // A spent submission means the player is done with the assessment even though
    // `duprRated` is still false: DUPR reports our push as an AI-annotated
    // rating, which `hasOfficialDuprRating` deliberately does not count. Reading
    // the profile alone is why a submitted player used to keep seeing the "get
    // rated" CTA.
    const duprDone = duprRated || duprSubmitted;
    const duprDashboardInPersonal =
        sport === "PICKLEBALL" && !duprLoading && duprDone;

    // The padel dashboard rides alongside the padel assessment entry rather
    // than replacing it: its DUPR twin appears only once the player is done
    // with the assessment, because submitting is once-only and the entry has
    // nothing left to offer after it. Padel has nothing to submit, so taking it
    // again stays on offer and both rows show together.
    // Withheld entirely during the trial: the padel dashboard is a results
    // surface, and the trial's whole premise is that results are not shown back
    // yet. Withheld while the phase is still loading too, for the same reason
    // the DUPR row waits on `duprLoading` — offering a row that then vanishes is
    // worse than a beat of nothing, and here the row would lead somewhere that
    // redirects.
    const padelDashboardInPersonal =
        sport === "PADEL" &&
        features.includes(PADEL_ASSESSMENT_FEATURE) &&
        !padelPhaseLoading &&
        padelPhase === "general";

    // The lacrosse trial has two things to offer, its Player and Goalie
    // workouts, and nothing else on this screen leads anywhere useful for it:
    // the sport-agnostic rows, clubs, community and Personal all list racquet
    // content. So lacrosse shows its own rows and nothing more.
    const lacrosseOnly = sport === "LACROSSE";

    const logInteractions = React.useMemo(() => {
        return features.includes("HOME_SCREEN_CLASSES");
    }, [features]);

    const mainWorkoutItemsFiltered = React.useMemo(() => {
        return withSegmentCollections(mainWorkoutItems, SEGMENT_COLLECTIONS)
            .filter((item) =>
                item.sports ? item.sports.includes(sport) : !lacrosseOnly,
            )
            .filter(
                (item) =>
                    !item.featureFlag || features.includes(item.featureFlag),
            )
            .filter(
                (item) =>
                    item.name !== "classes_at_location" || homeClubClasses,
            )
            .flatMap((item) => {
                if (item.name === "padel_assessment") {
                    // Badged "Beta" for the length of the trial, so a player
                    // knows what they are opting into before they tap — the
                    // assessment asks for their WPR and shows no score back,
                    // and both are easier to accept when flagged up front.
                    // Defer while the phase is unknown, the same way the DUPR
                    // entry defers on `duprLoading`, rather than badging and
                    // then un-badging.
                    if (padelPhaseLoading) return [];
                    return [
                        {
                            ...item,
                            badge:
                                padelPhase === "general" ? item.badge : "Beta",
                        },
                    ];
                }
                if (item.name !== "long_form_assessment") return [item];
                // Availability is the sport alone (`sports: ["PICKLEBALL"]` on
                // the item, applied by the filter above) — the assessment is no
                // longer gated on a feature.
                //
                // Defer until DUPR status is known (avoids a title flash), then
                // title by DUPR status.
                if (duprLoading) return [];
                // A player who has submitted has spent their one submission, so
                // there is nothing for this entry to offer. It drops out and the
                // DUPR Dashboard takes its place under Personal (see
                // duprDashboardInPersonal). Retaking stays reachable from the
                // dashboard itself.
                if (duprSubmitted) return [];
                // Officially-rated players keep the entry — they may retake,
                // subject to the server's retake throttle — but the copy shifts
                // from "get rated" to "retake", since they already have one.
                return [
                    {
                        ...item,
                        label: duprAssessmentLabel({ duprRated, duprLinked }),
                    },
                ];
            });
    }, [
        sport,
        lacrosseOnly,
        features,
        homeClubClasses,
        duprLoading,
        duprRated,
        duprLinked,
        duprSubmitted,
        padelPhase,
        padelPhaseLoading,
    ]);

    const logInteraction = React.useCallback(
        (name: string, context: Context) => {
            if (logInteractions) {
                userMonitor.addAction(name, context);
            }
        },
        [logInteractions],
    );

    const handleWorkoutItemClick = (
        item: (typeof mainWorkoutItemsFiltered)[0],
    ) => {
        if (item.setFilter) {
            item.setFilter();
        }
        logInteraction("Workout Category Selected", { value: item.label });
        // Tag navigation from the home screen so destinations (e.g. the
        // Performance Dashboard) can attribute the entry path on their
        // drop-off funnel events.
        navigate(item.navUrl, { state: { source: "home" } });
    };

    const handleCommunityClick = (providerId: number, name: string) => {
        FilterState.setFilter("search", {
            appId: null,
            position: false,
            providerIds: [providerId],
            excludeProviderIds: [],
            tagIds: [],
            contentMode: "mixed",
        });
        logInteraction("Community Workouts Selected", { value: name });
        navigate("/content/apps/workouts/search");
    };

    const handleAllCommunityClick = () => {
        FilterState.setFilter("search", {
            appId: null,
            position: false,
            providerIds: [],
            excludeProviderIds: [],
            tagIds: [],
            contentMode: "communityOnly",
        });
        logInteraction("All Community Workouts Selected", {});
        navigate("/content/apps/workouts/search");
    };

    const handlePersonalItemClick = (item: PersonalItem) => {
        // The question is whether the player has given us an APTA number, not
        // whether we could compute a PTI from it. Those differ: a linked player
        // with no matches attributed to them yet has no rating, and gating on
        // `rating` asked them to re-enter a number they already had, on every
        // tap, forever (issue #8335). Only "loaded" is an answer: on "error" we
        // never learned whether they have a number, so we fall through to
        // /player-pti, which reports its own failures, rather than ask.
        if (item.name === "pti_stats" && ptiState === "loaded" && !hasAptaId) {
            logInteraction("PTI Selected - No APTA ID", {});
            setDialogType("APTAId");
            return;
        }
        if (item.setFilter) {
            item.setFilter();
        }
        logInteraction("Personal Content Selected", { value: item.label });
        navigate(item.navUrl);
    };

    const filteredPersonalItems = React.useMemo(() => {
        const base = personalItems
            .filter(
                (item) => !item.sportFilter || item.sportFilter.includes(sport),
            )
            // Defer the PTI entry until its state is settled, the same way the
            // DUPR assessment entry defers on duprLoading. Its click behaviour
            // and its trailing PTI chip both read this state, so showing it
            // early means either a mistap into the wrong dialog or a chip that
            // pops in a beat later.
            // Withheld only while pending — on "error" the entry stays,
            // navigating to /player-pti, which reports its own failures. Hiding
            // it there would leave no way to reach PTI from home.
            .filter(
                (item) => item.name !== "pti_stats" || ptiState !== "loading",
            );
        if (padelDashboardInPersonal) {
            return [
                {
                    name: "padel_dashboard",
                    label: "Padel Dashboard",
                    subtitle: "Your assessment results",
                    navUrl: "/padel-dashboard",
                },
                ...base,
            ];
        }
        if (!duprDashboardInPersonal) return base;
        // Rated players get the DUPR Dashboard as their assessment-adjacent
        // Personal entry. A submitted player has no main-list entry at all; an
        // officially-rated one keeps a "retake" entry alongside this.
        return [
            {
                name: "dupr_dashboard",
                label: "DUPR Dashboard",
                subtitle: "Your rating, match history and reliability",
                navUrl: "/dupr-dashboard",
            },
            ...base,
        ];
    }, [sport, duprDashboardInPersonal, padelDashboardInPersonal, ptiState]);

    return (
        <>
            {/* Main Workout Cards */}
            <Stack spacing={1.5} sx={{ marginBottom: "25px" }}>
                {mainWorkoutItemsFiltered.map((item) => (
                    <WorkoutListCard
                        key={item.name}
                        icon={
                            item.name.startsWith(
                                SEGMENT_COLLECTION_ITEM_PREFIX,
                            ) ? (
                                <GroupsIcon
                                    sx={{
                                        color: item.iconColor,
                                        fontSize: "1.5rem",
                                    }}
                                />
                            ) : (
                                iconMap[item.name]
                            )
                        }
                        title={item.label}
                        subtitle={item.subtitle}
                        badge={item.badge}
                        onClick={() => handleWorkoutItemClick(item)}
                    />
                ))}
                {!lacrosseOnly && homeClub && homeClubWorkouts && (
                    <ClubCard
                        clubType="home"
                        providerName={homeClub.name}
                        providerLabel={homeClub.label}
                        onClick={() =>
                            handleCommunityClick(
                                homeClub.providerId,
                                homeClub.label,
                            )
                        }
                    />
                )}
                {!lacrosseOnly && trainerClub && (
                    <ClubCard
                        clubType="connected"
                        providerName={trainerClub.name}
                        providerLabel={trainerClub.label}
                        onClick={() =>
                            handleCommunityClick(
                                trainerClub.providerId,
                                trainerClub.label,
                            )
                        }
                    />
                )}
                {!lacrosseOnly && (
                    <SimpleCard
                        icon={
                            <GroupsIcon
                                sx={{ color: "#ffffff", fontSize: "1.5rem" }}
                            />
                        }
                        label="Community Published Workouts"
                        subtitle="Public drills created by other clubs and players"
                        dim
                        onClick={handleAllCommunityClick}
                    />
                )}
            </Stack>

            {!lacrosseOnly && (
                <>
                    {/* Personal Section */}
                    <SectionHeader title="Personal" />
                    <Stack spacing={1.5}>
                        {filteredPersonalItems.map((item) => (
                            <SimpleCard
                                key={item.name}
                                icon={personalIconMap[item.name]}
                                label={item.label}
                                subtitle={item.subtitle}
                                onClick={() => handlePersonalItemClick(item)}
                                endIcon={
                                    item.name === "pti_stats" && rating ? (
                                        <Chip
                                            size="small"
                                            label={
                                                <>
                                                    {rating.value}{" "}
                                                    <PtiTrendIcon
                                                        trend={rating.trend}
                                                        sx={{
                                                            fontSize: "1.2rem",
                                                            verticalAlign:
                                                                "middle",
                                                        }}
                                                    />
                                                </>
                                            }
                                            sx={(theme) => ({
                                                px: 1,
                                                textDecoration: "none",
                                                background:
                                                    theme.palette.background
                                                        .default,
                                                color: theme.palette.text
                                                    .primary,
                                            })}
                                        />
                                    ) : undefined
                                }
                            />
                        ))}
                    </Stack>
                </>
            )}
        </>
    );
}
